import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Mic, Volume2, Sparkles } from 'lucide-react';
import freshroadLogo from '../images/Freshroad-Health–health-thicker.png';

const ProactiveFQHC = () => {
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm Maya, your AI Assistant for Proactive FQHC. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);
  const lastAutoPlayedIndexRef = useRef(-1);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const stopTimeoutRef = useRef(null);
  const audioRef = useRef(null);
  const fileId = "6949386ac779ecf0b3624675"; // Fixed knowledge base file ID

  // Default system prompt for Proactive FQHC
  const defaultSystemPrompt = `You are Maya, the AI Assistant embedded on the website proactivefqhc.org for Proactive FQHC. Your role is to help visitors learn about proactive health screening, FQHCs (Federally Qualified Health Centers), and Proactive FQHC's mission.

CRITICAL: You have access to TWO sources of information:
1. DOCUMENT CONTEXT from the knowledge base (when available)
2. The following information about Proactive FQHC from their website (https://www.proactivefqhc.org) - THIS INFORMATION IS ALWAYS AVAILABLE TO YOU:

About Proactive FQHC:
- Proactive FQHC Inc. is a nonprofit, tax-exempt charitable organization (EIN: 99-2661952) under Section 501(c)(3) of the Internal Revenue Code
- It is a dedicated non-profit organization advocating for and supporting Federally Qualified Health Centers (FQHCs) nationwide
- Mission: Enhance community health through proactive health screenings, ensuring early detection and better patient outcomes
- Primary objectives: Advocate for FQHCs nationwide and to Congressional legislators, improve financial incentives for proactive health screening, encourage FQHCs to increase proactive patient screening for significant diseases such as diabetes, diabetic retinopathy, congestive heart failure, and similar diagnoses

Proactive Screening Services:
- Preventive services help you stay healthy, proactively detect health problems early, determine the most effective treatments, and prevent certain diseases
- Services include exams, lab tests, screenings, health monitoring programs, counseling, and education
- Locations: Clinic office, lab specimen collection, imaging units (CT scan, X-ray), and home-based telehealth and monitoring equipment
- Proactive healthcare is vital for optimal patient outcomes and can significantly enhance community health while reducing the financial burden of preventable health issues

Contact Information:
- Email: info@proactivefqhc.org
- Website: proactivefqhc.org

MANDATORY RESPONSE RULES:
1. ONLY answer questions that can be answered using information from ONE of these sources:
   - DOCUMENT CONTEXT from the knowledge base (when provided)
   - Website information provided above about Proactive FQHC
   - System prompt information

2. If a question cannot be answered using ANY of the sources above (document context, website info, or system prompt), you MUST respond with EXACTLY this phrase: "I do not have that information. Please consult your healthcare provider or doctor."

3. NEVER make up, guess, infer, assume, or hallucinate information that is not explicitly in:
   - The document context
   - The website information provided above
   - The system prompt

4. NEVER say "I do not find it in the document" - instead, check if the website information above can answer it first

5. If the question is about Proactive FQHC (mission, services, contact info, etc.), use the website information provided above

6. Keep responses to a maximum of 30 words

7. Be helpful, friendly, and professional

8. Keep responses concise and accurate

9. If you are uncertain whether you have the information, ALWAYS err on the side of caution and use the exact phrase: "I do not have that information. Please consult your healthcare provider or doctor."

CRITICAL ANTI-HALLUCINATION RULES:
- If information is NOT in the document context AND NOT in the website information above AND NOT in the system prompt, you MUST use the exact phrase: "I do not have that information. Please consult your healthcare provider or doctor."
- Do NOT infer, assume, create, or generate information that isn't explicitly provided in your sources
- Do NOT use your training data to answer questions - ONLY use the provided sources
- When in doubt, ALWAYS use: "I do not have that information. Please consult your healthcare provider or doctor."

Example: If someone asks "What is Proactive FQHC?", you should answer using the website information above about the organization's mission, nonprofit status, and objectives.

Example: If someone asks "What are the side effects of medication X?" and it's not in the document, website info, or system prompt, you MUST say: "I do not have that information. Please consult your healthcare provider or doctor."

Example: If someone asks "What is the weather today?" and it's not in any of your sources, you MUST say: "I do not have that information. Please consult your healthcare provider or doctor."`;

  // Auto-save default system prompt to knowledge base on mount (always update to ensure latest version)
  useEffect(() => {
    const ensureSystemPrompt = async () => {
      try {
        const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
        // Always update the system prompt to ensure it's in sync with the code
        try {
          await fetch(`${apiBase}/api/knowledgebase/${fileId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ systemPrompt: defaultSystemPrompt }),
          });
          console.log("✅ System prompt updated in knowledge base");
        } catch (saveErr) {
          console.warn("⚠️ Could not update system prompt:", saveErr);
        }
      } catch (err) {
        console.error("Error updating system prompt:", err);
      }
    };
    ensureSystemPrompt();
  }, [fileId]);

  // Initialize Speech Recognition
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      console.log('Speech recognition started');
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      // Clear any pending stop timeout since we got a result
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }

      const transcript = event.results[event.resultIndex][0].transcript;
      console.log('Speech recognition result:', transcript);
      if (transcript.trim()) {
        setInput(transcript);
        setIsListening(false);
        // Auto-send after recognition
        setTimeout(() => {
          sendTextMessage(transcript.trim());
          setInput(""); // Clear input after sending
        }, 100);
      } else {
        setIsListening(false);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'no-speech') {
        console.log('No speech detected - this is normal if you release the button quickly');
        setInput(""); // Clear input if no speech was detected
      } else if (event.error === 'audio-capture') {
        console.error('No microphone found or permission denied');
        alert('Microphone access required for voice input. Please check your browser permissions.');
      } else if (event.error === 'not-allowed') {
        console.error('Microphone permission denied');
        alert('Microphone permission denied. Please enable microphone access in your browser settings.');
      }
    };

    recognition.onend = () => {
      console.log('Speech recognition ended');
      // Clear any pending stop timeout
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      // Clear any pending stop timeout
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, []);

  // Start/stop voice recording (push-to-talk)
  const startListening = () => {
    if (!recognitionRef.current || sending) {
      console.log('Cannot start listening:', { hasRecognition: !!recognitionRef.current, sending });
      return;
    }

    // If already listening, don't try to start again
    if (isListening) {
      console.log('Already listening, ignoring start request');
      return;
    }

    try {
      console.log('Starting speech recognition...');
      recognitionRef.current.start();
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      setIsListening(false);
      // If it's already started, that's okay - just update state
      if (err.name === 'InvalidStateError' && err.message.includes('already started')) {
        console.log('Recognition already started, setting state to listening');
        setIsListening(true);
      }
    }
  };

  const stopListening = () => {
    if (!recognitionRef.current || !isListening) return;

    // Clear any pending stop timeout
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }

    try {
      console.log('Stopping speech recognition...');
      // Add a small delay before stopping to allow speech to be captured
      // The recognition needs time to process the audio
      stopTimeoutRef.current = setTimeout(() => {
        try {
          recognitionRef.current?.stop();
          stopTimeoutRef.current = null;
        } catch (err) {
          console.error('Error stopping speech recognition in timeout:', err);
          setIsListening(false);
        }
      }, 300); // 300ms delay to allow speech capture
    } catch (err) {
      console.error('Error stopping speech recognition:', err);
      setIsListening(false);
    }
  };

  // Send message to text chatbot API (text-only response, no TTS)
  const sendTextMessage = async (messageText) => {
    if (sending || !messageText.trim()) return;

    const userMessage = { role: "user", content: messageText.trim() };

    // Add user message to chat immediately
    setMessages((prev) => {
      const newHistory = [...prev, userMessage];

      // Make API request after state update
      setSending(true);
      (async () => {
        try {
          const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
          const res = await fetch(`${apiBase}/api/chatbot-public`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fileId: fileId,
              query: messageText.trim(),
              messages: newHistory,
            }),
          });

          const data = await res.json();
          if (!res.ok || !data?.success) {
            console.error('Chatbot API error:', res.status, data);
            const errorMsg = data?.message || `Error: ${res.status}`;
            setMessages((m) => [
              ...m,
              { role: "assistant", content: `Sorry — I ran into an error: ${errorMsg}` },
            ]);
          } else {
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
          }
        } catch (err) {
          console.error('Chatbot API request error:', err);
          setMessages((m) => [
            ...m,
            { role: "assistant", content: `Network error: ${err.message}. Please try again.` },
          ]);
        } finally {
          setSending(false);
        }
      })();

      return newHistory;
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-play AI responses when enabled
  useEffect(() => {
    if (!autoPlayEnabled || messages.length === 0) return;
    
    const lastIndex = messages.length - 1;
    const lastMessage = messages[lastIndex];
    
    // Only auto-play new assistant messages that haven't been played yet (skip initial greeting at index 0)
    if (lastMessage.role === "assistant" && lastIndex > lastAutoPlayedIndexRef.current && lastIndex > 0) {
      lastAutoPlayedIndexRef.current = lastIndex;
      // Use setTimeout to ensure state is updated and avoid re-render issues
      setTimeout(() => {
        playAIResponse(lastMessage.content, lastIndex);
      }, 500); // Small delay to ensure UI is updated
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, autoPlayEnabled]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const sendMessage = () => {
    const messageText = input.trim();
    if (!messageText || sending) return;

    setInput("");
    sendTextMessage(messageText);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const endSession = async () => {
    // Stop any playing audio immediately
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setPlayingAudioId(null);
    
    // Stop speech recognition if active
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors
      }
    }
    setIsListening(false);
    
    // Clear any pending stop timeout
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }

    if (messages.length <= 1) {
      // Only has the initial greeting, no need to save
      setChatbotOpen(false);
      setMessages([
        {
          role: "assistant",
          content: "Hi! I'm Maya, your AI Assistant for Proactive FQHC. How can I help you today?",
        },
      ]);
      setInput("");
      lastAutoPlayedIndexRef.current = -1;
      return;
    }

    try {
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      
      const res = await fetch(`${apiBase}/api/chatbot-session-public`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          scriptData: null, // Public chatbot, no script data
          messages: messages
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        // Reset chat
        setMessages([
          {
            role: "assistant",
            content: "Hi! I'm Maya, your AI Assistant for Proactive FQHC. How can I help you today?",
          },
        ]);
        setInput("");
        lastAutoPlayedIndexRef.current = -1;
        setChatbotOpen(false);
        alert('Session saved successfully!');
      } else {
        console.error('Failed to save session:', data.message);
        alert('Failed to save session. Please try again.');
      }
    } catch (err) {
      console.error('Error saving session:', err);
      alert('Error saving session. Please try again.');
    }
  };

  // Play AI response using Kokoro TTS
  const playAIResponse = async (text, messageId) => {
    if (playingAudioId === messageId) {
      // If already playing this message, stop it
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingAudioId(null);
      return;
    }

    try {
      setPlayingAudioId(messageId);
      
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const res = await fetch(`${apiBase}/api/kokoro-tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }), // Voice ID comes from env var (ELEVENLABS_VOICE_ID)
      });

      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to generate audio');
      }

      // Convert base64 to blob (format is wav for Kokoro)
      const audioBlob = await fetch(`data:audio/${data.format || 'wav'};base64,${data.audio}`).then(r => r.blob());
      const audioUrl = URL.createObjectURL(audioBlob);

      // Create and play audio
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingAudioId(null);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        setPlayingAudioId(null);
        URL.revokeObjectURL(audioUrl);
        console.error('Error playing audio');
      };

      await audio.play();
    } catch (err) {
      console.error('Error playing AI response:', err);
      setPlayingAudioId(null);
      alert('Failed to play audio. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Proactive FQHC Inc</h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#home" className="text-gray-700 hover:text-gray-900">Home</a>
              <a href="#maps" className="text-gray-700 hover:text-gray-900">Maps</a>
              <a href="#about" className="text-gray-700 hover:text-gray-900">About Us</a>
              <a href="#projects" className="text-gray-700 hover:text-gray-900">Projects</a>
              <a href="#news" className="text-gray-700 hover:text-gray-900">News</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Supporting FQHCs Improving Health Through Proactive Screening
          </h2>
          <p className="text-xl md:text-2xl mb-8">
            Helping FQHCs improve patient care with earlier detection using efficiently targeted and scheduled proactive health screenings.
          </p>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">About Us</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Committed to Health Innovation</h3>
          </div>
          <div className="max-w-3xl mx-auto">
            <p className="text-lg text-gray-700 mb-4">
              Proactive FQHC is a dedicated non-profit organization advocating for and supporting Federally Qualified Health Centers (FQHCs) nationwide. Our mission is to enhance community health through proactive health screenings, ensuring early detection and better patient outcomes.
            </p>
            <p className="text-lg text-gray-700">
              Our primary objectives are to advocate for FQHCs nationwide and to Congressional legislators. Our aim is to improve financial incentives for proactive health screening, encouraging FQHCs to increase proactive patient screening for significant diseases such as diabetes, diabetic retinopathy, congestive heart failure, and similar diagnoses.
            </p>
          </div>
        </div>
      </section>

      {/* Proactive Screening Section */}
      <section id="projects" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Proactive Screening</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Preventive and Screening Services</h3>
          </div>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 mb-8">
              Preventive services help you stay healthy, proactively detect health problems early, determine the most effective treatments, and prevent certain diseases. Preventive services include exams, lab tests, and screenings. They also include programs for health monitoring, and counseling, and education to help you take care of your own health.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-2">Clinic office</h4>
                <p className="text-gray-700">Care providers primary location for patient visits</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-2">Lab specimen</h4>
                <p className="text-gray-700">At care provider location to collect patient samples to be sent for laboratory analysis with results reported</p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-2">Imaging unit</h4>
                <p className="text-gray-700">Some health issues addressed in the office visit also require CT scan, X-ray, or other imaging performed by trained technicians using the specialized equipment</p>
              </div>
            </div>
            <div className="bg-green-50 p-6 rounded-lg mb-8">
              <h4 className="font-semibold text-lg mb-2">Home</h4>
              <p className="text-gray-700">Care provider orders telehealth and monitoring equipment to communicate and collect physiologic and therapeutic data</p>
            </div>
            <p className="text-lg text-gray-700">
              Proactive healthcare is vital for optimal patient outcomes. By implementing a value-based proactive care model, FQHCs can significantly enhance community health and consecutively reduce the financial burden of preventable health issues.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Explore Health Centers on the Interactive Map</h2>
          <p className="text-lg text-gray-700 mb-8">
            To find help centers easily, check them out on our interactive map. Click below to navigate.
          </p>
          <button className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition">
            View Map
          </button>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact us</h2>
            <p className="text-lg text-gray-700 mb-8">
              Fill out the form or contact us by email and we will contact you as soon as possible.
            </p>
            <a href="mailto:info@proactivefqhc.org" className="text-blue-600 hover:text-blue-800 text-lg">
              info@proactivefqhc.org
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="mb-4">
              Proactive FQHC Inc. is a nonprofit, tax-exempt charitable organization (EIN: 99-2661952) under Section 501(c)(3) of the Internal Revenue Code.
            </p>
            <p className="text-sm text-gray-400">
              © 2025 Proactive FQHC. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Chatbot Widget */}
      {!chatbotOpen ? (
        <button
          onClick={() => setChatbotOpen(true)}
          className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition z-50 flex items-center gap-2"
        >
          <MessageSquare size={24} />
          <span className="hidden sm:inline">Chat with us</span>
        </button>
      ) : (
        <div className="fixed bottom-6 right-6 w-[500px] h-[700px] bg-white rounded-lg shadow-2xl z-50 flex flex-col border border-gray-200">
          {/* Chatbot Header */}
          <div className="bg-blue-600 text-white p-4 rounded-t-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {/* Portrait/Avatar */}
                <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center overflow-hidden border-2 border-white border-opacity-30">
                  <MessageSquare size={24} className="text-white" />
                </div>
                {/* Name and AI Indicator */}
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">Maya</h3>
                    {/* AI Indicator */}
                    <div className="flex items-center gap-1 bg-white bg-opacity-20 px-2 py-0.5 rounded-full">
                      <Sparkles size={12} className="text-yellow-300" />
                      <span className="text-xs font-medium">AI</span>
                    </div>
                  </div>
                  <span className="text-xs text-blue-100">AI Assistant</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Auto-play toggle switch */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm">Auto-play</span>
                  <div className="relative inline-block w-11 h-6">
                    <input
                      type="checkbox"
                      checked={autoPlayEnabled}
                      onChange={(e) => setAutoPlayEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-blue-400 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-white rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-800"></div>
                  </div>
                </label>
                {messages.length > 1 && (
                  <button
                    onClick={endSession}
                    className="inline-flex items-center gap-1 text-sm bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-white"
                    title="End session and save chat log"
                  >
                    End Session
                  </button>
                )}
                <button
                  onClick={() => setChatbotOpen(false)}
                  className="hover:bg-blue-700 rounded p-1 transition"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${msg.role === "user"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-800 border border-gray-200"
                    }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1">{msg.content}</div>
                    {msg.role === "assistant" && (
                      <button
                        onClick={() => playAIResponse(msg.content, idx)}
                        className={`flex-shrink-0 p-1.5 rounded-full transition ${
                          playingAudioId === idx
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                        title={playingAudioId === idx ? "Stop reading" : "Read aloud"}
                      >
                        <Volume2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 border border-gray-200 rounded-lg p-3">
                  Thinking...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isListening ? "Listening..." : "Type your message..."}
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={sending || isListening}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || sending || isListening}
                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <Send size={20} />
              </button>
            </div>
            
            {/* Voice Input Button - More Prominent and Intuitive */}
            <div className="flex flex-col items-center gap-2 mb-2">
              <button
                onMouseDown={startListening}
                onMouseUp={stopListening}
                onMouseLeave={stopListening}
                onTouchStart={(e) => {
                  e.preventDefault();
                  startListening();
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  stopListening();
                }}
                disabled={sending}
                className={`relative flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isListening
                    ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/50 scale-105"
                    : "bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg"
                }`}
              >
                {isListening ? (
                  <>
                    <div className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-75"></div>
                    <Mic size={20} className="relative z-10" />
                    <span className="relative z-10 text-sm font-semibold">Listening...</span>
                  </>
                ) : (
                  <>
                    <Mic size={20} />
                    <span className="text-sm font-semibold">Hold to Speak</span>
                  </>
                )}
              </button>
              {!isListening && (
                <p className="text-xs text-gray-500 text-center">
                  Press and hold the button to record your message
                </p>
              )}
              {isListening && (
                <p className="text-sm text-red-600 font-medium text-center animate-pulse">
                  🎤 Recording... Release when done
                </p>
              )}
            </div>

            {/* Powered by Freshroad Health Logo */}
            <div className="flex items-center justify-center gap-2 pt-2 border-t border-gray-100">
              <span className="text-xs text-gray-500">Powered by</span>
              <img 
                src={freshroadLogo} 
                alt="Freshroad Health" 
                className="h-4 object-contain opacity-70"
              />
              <span className="text-xs text-gray-500 font-medium"></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

//

export default ProactiveFQHC;



